/**
 * Spends a size budget where it costs the least quality.
 *
 * Every item (a picture, or a page turned into a picture) can be stored in several ways, each with a size
 * and a measured loss of quality. Giving every item the same setting wastes bytes: a busy photo and a
 * plain page lose very different amounts of quality for the same saving. The best split is the one where
 * the last byte saved costs every item the same quality ("equal slope" in rate-distortion theory): start
 * from the sharpest choice everywhere, then keep taking the step that saves the most bytes per unit of
 * quality lost, across all items, until the total fits.
 */

export interface Candidate {
  /** Size in bytes if this item is stored this way. */
  bytes: number;
  /** Quality lost, 0 (identical to the original) upward. Comparable between items. */
  distortion: number;
}

export interface AllocateResult {
  /** For every item, the index of the chosen candidate. */
  choice: number[];
  /** Total bytes of the chosen candidates plus the fixed bytes. */
  total: number;
  /** The total is within the budget. */
  fits: boolean;
}

/** The points worth considering for one item: sharpest first, each one smaller and no better than the one before, on the convex hull. */
export function hullOf(candidates: Candidate[]): number[] {
  const order = candidates.map((_, i) => i).sort((a, b) => candidates[b].bytes - candidates[a].bytes || candidates[a].distortion - candidates[b].distortion);
  // Pareto: walking from largest to smallest, keep a point only if it is smaller AND does not look better than what we kept (it never should).
  const pareto: number[] = [];
  for (const i of order) {
    const last = pareto[pareto.length - 1];
    if (last === undefined) { pareto.push(i); continue; }
    if (candidates[i].bytes >= candidates[last].bytes) continue; // same size: the first (lower distortion) stays
    if (candidates[i].distortion < candidates[last].distortion) { pareto.pop(); pareto.push(i); continue; } // smaller AND better: it replaces
    pareto.push(i);
  }
  // Convex hull in (bytes saved, distortion): slopes must increase.
  const hull: number[] = [];
  for (const i of pareto) {
    while (hull.length >= 2) {
      const a = candidates[hull[hull.length - 2]], b = candidates[hull[hull.length - 1]], c = candidates[i];
      const s1 = (b.distortion - a.distortion) / Math.max(1, a.bytes - b.bytes);
      const s2 = (c.distortion - b.distortion) / Math.max(1, b.bytes - c.bytes);
      if (s2 <= s1) hull.pop(); else break;
    }
    hull.push(i);
  }
  return hull;
}

/**
 * Chooses a candidate for every item so the total fits `budgetBytes` with the least quality lost.
 * `fixedBytes` is everything that is not an item (page text, structure). Candidate 0 of the hull is the
 * starting point, so list the sharpest / original option among the candidates.
 * If even the smallest choices do not fit, every item gets its smallest candidate and `fits` is false.
 */
export function allocate(items: Candidate[][], budgetBytes: number, fixedBytes = 0): AllocateResult {
  const hulls = items.map(hullOf);
  const pos = hulls.map(() => 0);
  let total = fixedBytes;
  hulls.forEach((h, k) => { total += items[k][h[0]].bytes; });

  // Every possible step, cheapest quality-per-byte first.
  const steps: { item: number; slope: number }[] = [];
  hulls.forEach((h, k) => {
    for (let j = 1; j < h.length; j++) {
      const a = items[k][h[j - 1]], b = items[k][h[j]];
      steps.push({ item: k, slope: (b.distortion - a.distortion) / Math.max(1, a.bytes - b.bytes) });
    }
  });
  // Stable by slope; within one item the slopes already increase, so its steps are taken in order.
  steps.sort((x, y) => x.slope - y.slope || x.item - y.item);

  for (const st of steps) {
    if (total <= budgetBytes) break;
    const h = hulls[st.item];
    const from = items[st.item][h[pos[st.item]]], to = items[st.item][h[pos[st.item] + 1]];
    total -= from.bytes - to.bytes;
    pos[st.item]++;
  }
  return { choice: hulls.map((h, k) => h[pos[k]]), total, fits: total <= budgetBytes };
}

/**
 * How different two same-sized greyscale pictures look, 0 (identical) to 1: one minus the mean structural
 * similarity (SSIM) over 8x8 blocks. Blur, blockiness and lost detail all raise it; a flat blank page stays near 0.
 */
export function ssimDistortion(a: Uint8Array | Uint8ClampedArray, b: Uint8Array | Uint8ClampedArray, width: number, height: number): number {
  const C1 = (0.01 * 255) ** 2, C2 = (0.03 * 255) ** 2;
  const B = 8;
  let sum = 0, blocks = 0;
  for (let by = 0; by + B <= height; by += B) {
    for (let bx = 0; bx + B <= width; bx += B) {
      let ma = 0, mb = 0;
      for (let y = 0; y < B; y++) for (let x = 0; x < B; x++) { const i = (by + y) * width + bx + x; ma += a[i]; mb += b[i]; }
      ma /= B * B; mb /= B * B;
      let va = 0, vb = 0, cov = 0;
      for (let y = 0; y < B; y++) for (let x = 0; x < B; x++) { const i = (by + y) * width + bx + x; const da = a[i] - ma, db = b[i] - mb; va += da * da; vb += db * db; cov += da * db; }
      va /= B * B - 1; vb /= B * B - 1; cov /= B * B - 1;
      sum += ((2 * ma * mb + C1) * (2 * cov + C2)) / ((ma * ma + mb * mb + C1) * (va + vb + C2));
      blocks++;
    }
  }
  return blocks ? Math.max(0, 1 - sum / blocks) : 0;
}

/** Greyscale (0.299 R + 0.587 G + 0.114 B) from canvas RGBA pixels. */
export function rgbaToGray(rgba: Uint8Array | Uint8ClampedArray): Uint8Array {
  const out = new Uint8Array(rgba.length / 4);
  for (let i = 0, j = 0; j < out.length; i += 4, j++) out[j] = (rgba[i] * 299 + rgba[i + 1] * 587 + rgba[i + 2] * 114) / 1000;
  return out;
}
